import { useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useRef } from "react";
import {
  CONFIGURATION_ENTITY_MAP,
  ENTITY_ACTIONS,
  createSyncApi,
  type ConfigurationEntityName,
} from "@repo/services";
import { configurationApi } from "~/api";

type SignalRContextType = ReturnType<typeof createSyncApi> | null;

const SignalRContext = createContext<SignalRContextType>(null);

const getQueryData = (oldData: unknown, action: string, newEntity: { id?: unknown }) => {
  if (!Array.isArray(oldData)) return oldData;
  const entityId = newEntity?.id;

  switch (action) {
    case ENTITY_ACTIONS.Created:
      if (oldData.some((item) => item.id === entityId)) return oldData;
      return [...oldData, newEntity];
    case ENTITY_ACTIONS.Updated:
    case ENTITY_ACTIONS.Complete:
      return oldData.map((item) => (item.id === entityId ? newEntity : item));
    case ENTITY_ACTIONS.Deleted:
      return oldData.filter((item) => item.id !== entityId);
    default:
      return oldData;
  }
};

export const ConfigurationSignalRProvider = ({ children }: { children: React.ReactNode }) => {
  const apiRef = useRef(createSyncApi(import.meta.env.VITE_CONFIG_API_BASE_URL));
  const queryClient = useQueryClient();

  useEffect(() => {
    const api = apiRef.current;

    api.start().catch((err) => {
      console.error("[SignalR] Failed to start connection:", err);
    });

    api.connection.onclose((error) => {
      if (error) {
        console.error("[SignalR] Connection closed with error:", error);
      }
    });

    api.connection.onreconnecting((error) => {
      console.warn("[SignalR] Reconnecting...", error);
    });

    api.connection.onreconnected(() => {
      console.log("[SignalR] Reconnected.");
    });

    const removeListener = api.onEntityChanged((data) => {
      const { typeNameConstant, entity, action } = data;
      const typedEntity = entity as { id?: unknown; workflowId?: number; brandId?: number };
      const apiPath = CONFIGURATION_ENTITY_MAP[typeNameConstant as ConfigurationEntityName];

      if (!apiPath) return;
      let queryKey: readonly unknown[];

      try {
        if (apiPath.includes("{workflowId}") && typedEntity?.workflowId) {
          queryKey = configurationApi.queryOptions(
            "get",
            apiPath as Parameters<typeof configurationApi.queryOptions>[1],
            {
              params: {
                path: { workflowId: typedEntity.workflowId },
              },
            } as unknown as Parameters<typeof configurationApi.queryOptions>[2]
          ).queryKey;
        } else if (apiPath.includes("{brandId}") && typedEntity?.brandId) {
          queryKey = configurationApi.queryOptions(
            "get",
            apiPath as Parameters<typeof configurationApi.queryOptions>[1],
            {
              params: {
                path: { brandId: typedEntity.brandId },
              },
            } as unknown as Parameters<typeof configurationApi.queryOptions>[2]
          ).queryKey;
        } else {
          queryKey = configurationApi.queryOptions(
            "get",
            apiPath as Parameters<typeof configurationApi.queryOptions>[1]
          ).queryKey;
        }
      } catch (_error) {
        return;
      }

      queryClient.setQueryData(queryKey, (oldData: unknown) => {
        if (!oldData) return undefined;
        return getQueryData(oldData, action, typedEntity);
      });

      if (typedEntity?.id) {
        queryClient.invalidateQueries({ queryKey });
      }

      queryClient.invalidateQueries({ queryKey });
    });

    return () => {
      removeListener();
    };
  }, [queryClient]);

  return <SignalRContext.Provider value={apiRef.current}>{children}</SignalRContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useConfigurationSignalR = (): SignalRContextType => {
  const context = useContext(SignalRContext);
  if (!context) throw new Error("useConfigurationSignalR must be used within SignalRProvider");
  return context;
};
